// framePlan — pure frame-timing helpers for the timelapse replay
// (growth-timelapse-share-2026-07-02.md §5.1 「フレーム化 / 長さ制御」+
// §5.2 「長さ/倍速指定」).
//
// The replay driver walks the merged event list and captures one frame
// every time the applied-event count crosses the next cutoff. Two modes:
//
//   - `duration` (既定): 「選択範囲を N 秒に収める」. Frame density is
//     **progress-based** — total frames = seconds × fps, each frame k is
//     captured after `ceil((k+1) · eventCount / frameCount)` events. Wall
//     clock is ignored entirely, so idle hours between strokes vanish for
//     free (数日ログ対応, no wall-clock frames).
//   - `multiplier`: 実描画時間ベースの ×N 倍速. Uses the server-stamped
//     event `ts` with the inter-event gap **clamped to 2 s max** (§5.2)
//     so a lunch break contributes at most 2 s of virtual time, then maps
//     virtual time → output frames at ×N speed.
//
// Both modes emit the same shape (`FramePlan.eventCutoffs`) so the replay
// driver has a single loop. Everything here is pure + unit-tested; the
// driver (`timelapseReplay.ts`) never re-implements timing math.

/** §5.2 mode selector (duration = 既定 / multiplier = 副モード). */
export type TimelapseMode =
  | { readonly kind: 'duration'; readonly seconds: number }
  | { readonly kind: 'multiplier'; readonly x: number };

/** Output frame rate default (§5.1 — 720p級 / 30fps 目安). */
export const DEFAULT_TIMELAPSE_FPS = 30;

/**
 * Inter-event gap clamp for multiplier mode (§5.2 — 間隔クランプ 最大2秒).
 * A gap longer than this contributes exactly this much virtual time.
 */
export const MAX_INTER_EVENT_GAP_MS = 2_000;

/**
 * **Absolute** hard ceiling on output length (§5.5, consult 2026-07-06).
 *
 * History: this was the X (Twitter) 140 s standard-upload limit. §5.5
 * relaxed the cap to a per-preset × per-platform value derived from a fixed
 * memory budget (`timelapseQuality.resolveTimelapseQuality`), because the
 * 「保存 → YouTube」動線 makes the 140 s X limit irrelevant. This constant is
 * now only the **runaway guard**: the real cap flows in as `maxFrames`
 * (= resolved `maxSeconds × fps`); this value bounds the plan when nobody
 * passes one (dev harness / direct `planFrames` calls) and is the ceiling
 * the quality resolver clamps its budget arithmetic to.
 *
 * When a multiplier plan would exceed `maxFrames`, the plan keeps the full
 * selected range and stretches the per-frame virtual step instead (= the
 * effective speed becomes faster than the requested ×N — preferable to
 * silently truncating the tail of the drawing).
 */
export const HARD_MAX_OUTPUT_SECONDS = 900;

export interface FramePlan {
  /** Number of frames the replay will yield (>= 1). */
  readonly frameCount: number;
  /**
   * `eventCutoffs[k]` = how many events (prefix of the merged, trimmed
   * list) must be applied before frame `k` is captured. Monotone
   * non-decreasing; the final entry always equals the total event count
   * so the last frame shows the finished picture.
   */
  readonly eventCutoffs: readonly number[];
  /** `frameCount / fps` — for the UI length preview. */
  readonly outputDurationSeconds: number;
  readonly fps: number;
}

export interface PlanFramesInput {
  readonly eventCount: number;
  /**
   * Server-stamped `ts` (epoch ms) per event, in the same order as the
   * merged event list. Required for `multiplier` mode (its whole point is
   * the real-time axis); ignored for `duration` mode.
   */
  readonly eventTimestampsMs?: readonly number[];
  readonly mode: TimelapseMode;
  /** Default {@link DEFAULT_TIMELAPSE_FPS}. */
  readonly fps?: number;
  /**
   * Frame-count ceiling. In production this is the resolved
   * `maxSeconds × fps` (per-preset / per-platform memory budget, §5.5,
   * threaded from `resolveTimelapseQuality`). Default (dev harness / direct
   * callers) {@link HARD_MAX_OUTPUT_SECONDS} × fps.
   */
  readonly maxFrames?: number;
}

/**
 * Compute the frame plan. Never returns 0 frames — an empty event list
 * still yields a single frame (the blank / trimmed-start canvas) so the
 * downstream encoder (T2) always has at least one sample to mux.
 *
 * Throws `TypeError` on programming errors (multiplier without
 * timestamps / length mismatch / non-positive seconds·x·fps) — these are
 * caller bugs, not runtime data conditions, matching the repo's
 * outcome-union-for-data / throw-for-bugs convention.
 */
export const planFrames = (input: PlanFramesInput): FramePlan => {
  const fps = input.fps ?? DEFAULT_TIMELAPSE_FPS;
  if (!Number.isFinite(fps) || fps <= 0) throw new TypeError(`planFrames: invalid fps ${fps}`);
  const maxFrames = input.maxFrames ?? Math.ceil(HARD_MAX_OUTPUT_SECONDS * fps);
  if (!Number.isInteger(maxFrames) || maxFrames < 1) {
    throw new TypeError(`planFrames: invalid maxFrames ${maxFrames}`);
  }
  const eventCount = input.eventCount;
  if (!Number.isInteger(eventCount) || eventCount < 0) {
    throw new TypeError(`planFrames: invalid eventCount ${eventCount}`);
  }

  if (input.mode.kind === 'duration') {
    return planByDuration(eventCount, input.mode.seconds, fps, maxFrames);
  }
  return planByMultiplier(input, fps, maxFrames);
};

const planByDuration = (
  eventCount: number,
  seconds: number,
  fps: number,
  maxFrames: number,
): FramePlan => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    throw new TypeError(`planFrames: invalid duration seconds ${seconds}`);
  }
  if (eventCount === 0) return singleFramePlan(fps);
  // Progress-based density: frames spread evenly over the event count.
  // No point emitting more frames than events (extra frames would be
  // byte-identical duplicates); the clamp keeps tiny logs snappy.
  const requested = Math.min(Math.max(1, Math.round(seconds * fps)), maxFrames);
  const frameCount = Math.min(requested, eventCount);
  const eventCutoffs: number[] = new Array(frameCount);
  for (let k = 0; k < frameCount; k++) {
    eventCutoffs[k] = Math.min(eventCount, Math.ceil(((k + 1) * eventCount) / frameCount));
  }
  eventCutoffs[frameCount - 1] = eventCount;
  return { frameCount, eventCutoffs, outputDurationSeconds: frameCount / fps, fps };
};

const planByMultiplier = (input: PlanFramesInput, fps: number, maxFrames: number): FramePlan => {
  if (input.mode.kind !== 'multiplier') throw new TypeError('planByMultiplier: wrong mode');
  const x = input.mode.x;
  if (!Number.isFinite(x) || x <= 0) {
    throw new TypeError(`planFrames: invalid multiplier x ${x}`);
  }
  const ts = input.eventTimestampsMs;
  if (ts === undefined || ts.length !== input.eventCount) {
    throw new TypeError(
      `planFrames: multiplier mode requires eventTimestampsMs (len ${ts?.length ?? 'none'} vs eventCount ${input.eventCount})`,
    );
  }
  if (input.eventCount === 0) return singleFramePlan(fps);

  // Virtual drawing-time axis: cumulative elapsed ms with each inter-event
  // gap clamped to MAX_INTER_EVENT_GAP_MS. Negative gaps (seq-reuse overlap
  // resolution can splice a post-restart timeline whose clock sits behind
  // the pre-restart tail) count as 0 — time never runs backwards.
  const elapsed = clampedElapsedMs(ts, MAX_INTER_EVENT_GAP_MS);
  const totalVirtualMs = elapsed[elapsed.length - 1] ?? 0;
  if (totalVirtualMs <= 0) {
    // Single-event log / all-identical timestamps: nothing to pace, show
    // the finished picture once.
    return {
      frameCount: 1,
      eventCutoffs: [input.eventCount],
      outputDurationSeconds: 1 / fps,
      fps,
    };
  }

  // Virtual ms consumed per output frame at ×N speed. If that would blow
  // the output-length cap, stretch the step so the WHOLE range still fits
  // (= effective speed > requested ×N; see HARD_MAX_OUTPUT_SECONDS).
  let stepMs = (1_000 * x) / fps;
  let frameCount = Math.max(1, Math.ceil(totalVirtualMs / stepMs));
  if (frameCount > maxFrames) {
    frameCount = maxFrames;
    stepMs = totalVirtualMs / frameCount;
  }

  const eventCutoffs: number[] = new Array(frameCount);
  let idx = 0;
  for (let k = 0; k < frameCount; k++) {
    const cut = (k + 1) * stepMs;
    while (idx < elapsed.length && (elapsed[idx] ?? 0) <= cut) idx++;
    eventCutoffs[k] = idx;
  }
  eventCutoffs[frameCount - 1] = input.eventCount;
  return { frameCount, eventCutoffs, outputDurationSeconds: frameCount / fps, fps };
};

/**
 * Cumulative virtual elapsed ms per event with gap clamping — exported
 * for direct unit testing (the multiplier plan is a thin mapping on top).
 * `out[0]` is always 0; `out[i] = out[i-1] + clamp(ts[i] - ts[i-1], 0, clampMs)`.
 */
export const clampedElapsedMs = (ts: readonly number[], clampMs: number): readonly number[] => {
  const out: number[] = new Array(ts.length);
  let acc = 0;
  for (let i = 0; i < ts.length; i++) {
    if (i > 0) {
      const gap = (ts[i] ?? 0) - (ts[i - 1] ?? 0);
      acc += Math.min(Math.max(gap, 0), clampMs);
    }
    out[i] = acc;
  }
  return out;
};

const singleFramePlan = (fps: number): FramePlan => ({
  frameCount: 1,
  eventCutoffs: [0],
  outputDurationSeconds: 1 / fps,
  fps,
});
